<template>
  <div class="px-6 py-10 min-h-screen">
    <div class="mx-auto">

      <!-- Header: Back + Account Info + Balances -->
      <div class="flex items-center justify-between mb-8">
        <!-- Left side: back + account selector -->
        <div class="flex flex-col items-left">
          <!-- Back arrow -->
          <button class="text-primary hover:text-orange-600 transition-colors">
            <component :is="ArrowLeft" class="w-6 h-6"/>
          </button>

          <!-- Account info -->
          <div class="mt-4 flex flex-col">
            <label for="account-select" class="text-gray-600 text-sm mb-1">Select Account</label>
            <select
                id="account-select"
                v-model="selectedAccountId"
                class="border rounded px-3 py-2 w-64"
            >
              <option v-for="acc in accounts" :key="acc.id" :value="acc.id">
                {{ acc.account_name }} - {{ acc.account_number }} ({{ acc.currency }})
              </option>
            </select>

            <!-- Account number badge -->
            <div v-if="selectedAccount" class="mt-2">
              <span class="inline-block bg-gray-800 text-white text-sm px-3 py-1 rounded-full">
                {{ selectedAccount.account_number }}
              </span>
            </div>
          </div>
        </div>

        <!-- Right side: balances -->
        <div class="flex space-x-8" v-if="selectedAccount">
          <!-- Accounting balance -->
          <div class="text-right">
            <div class="text-lg font-medium text-gray-800">
              {{ selectedAccount.currency }} {{ selectedAccount.accounting_balance }}
            </div>
            <div class="text-sm text-primary">Accounting</div>
          </div>

          <!-- Available balance -->
          <div class="text-right">
            <div class="text-lg font-medium text-gray-800">
              {{ selectedAccount.currency }} {{ selectedAccount.available_balance }}
            </div>
            <div class="text-sm text-primary">Available</div>
          </div>
        </div>
      </div>

      <!-- Boxes / Sections -->
      <div class="p-6">
        <div class="flex justify-center space-x-4 mb-6">
          <button
              v-for="item in items"
              :key="item.id"
              :class="[
              'flex flex-col items-center bg-white px-6 py-4 border rounded-lg shadow-md cursor-pointer transition-colors hover:border-gray-400 hover:bg-white hover:shadow-md hover:text-gray-800',
              selected === item.id
                ? 'border-gray-400 bg-white shadow-md'
                : 'border-transparent text-primary'
            ]"
              @click="selected = item.id"
          >
            <component :is="item.icon" alt="" class="w-8 h-8 mb-2"/>
            <span :class="selected === item.id ? 'text-gray-800' : 'text-orange-400'" class="text-sm">
              {{ item.label }}
            </span>
          </button>
        </div>

        <!-- Selected Component -->
        <div class="">


          <!-- Steps -->
          <div class="flex justify-between items-center border-b border-gray-200 mb-8">
            <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
              <div
                  :class="['w-8 h-8 flex items-center justify-center rounded-full font-bold z-10', activeStep === 1 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">
                1
              </div>
              <span
                  :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 1 }]">Fill Data</span>
              <!--                <div class="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 z-0"></div>-->
            </div>

            <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
              <div
                  :class="['w-8 h-8 flex items-center justify-center rounded-full z-10', activeStep === 2 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">
                2
              </div>
              <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 2 }]">Check the Operation</span>
            </div>

            <div class="w-fit bg-gray-100 transform translate-y-1/2 px-2 flex items-center justify-center relative">
              <div
                  :class="['w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-700 rounded-full z-10', activeStep === 3 ? 'bg-gray-800 text-white': 'bg-gray-200 text-gray-700']">
                3
              </div>
              <span :class="['ml-2 text-gray-500', { 'font-medium text-gray-900': activeStep === 3 }]">Confirmation</span>
            </div>
          </div>


          <!-- Transfer form -->
          <Transition name="slide-left">
            <div v-if="!isPanelOpen && !showTransactionConfirm && selected === 'european'" class="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div class="space-y-6">
                <div>
                  <h3 class="text-sm font-medium text-gray-600 mb-4 tracking-wide">TRANSFER TO WHOM?</h3>
                  <div class="relative">
                    <input
                        v-model="searchBeneficiary"
                        @input="debouncedSearch"
                        class="w-full px-4 py-3 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="Search Beneficiary"
                        type="text"
                    />
                    <svg class="absolute left-3 top-3.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor"
                         viewBox="0 0 24 24">
                      <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" stroke-linecap="round"
                            stroke-linejoin="round" stroke-width="2"></path>
                    </svg>
                  </div>

                  <div class="mb-6">
                    <BeneficiarySelector
                        v-model="selectedBeneficiary"
                        :beneficiaries="beneficiaryList"
                        @select-beneficiary="onBeneficiarySelected"
                        @add-new-beneficiary="openNewBeneficiaryForm"
                    />
                    <NewBeneficiaryModal :isOpen="isModalOpen" @close="isModalOpen = false" @submitted="searchBeneficiaries"/>
                  </div>
                </div>

                <!-- Account to Credit -->
                <div>
                  <h3 class="text-sm font-medium text-gray-600 mb-4 tracking-wide">ACCOUNT TO CREDIT</h3>
                  <div class="space-y-4">
                    <FloatingLabelInput v-model="iban" input-id="iban-field" label="IBAN *"/>
                    <FloatingLabelInput v-model="swiftCode" input-id="swift-field" label="BIC / SWIFT Code *"/>
                    <FloatingLabelInput v-model="beneficiaryName" input-id="name-field" label="Beneficiary name *"/>
                  </div>
                </div>
              </div>

              <div class="space-y-6">
                <!-- How Much -->
                <div>
                  <h3 class="text-sm font-medium text-gray-600 mb-4 tracking-wide">HOW MUCH?</h3>
                  <div class="flex items-center gap-x-2 relative">
                    <FloatingLabelInput v-model="transferValue" class="w-full" label="Value *" min="0" type="number"/>
                    <button class="text-gray-600 bg-gray-200 rounded px-3 py-3" type="button">EUR</button>
                  </div>
                  <div class="mt-4">
                    <FloatingLabelInput v-model="description" input-id="desc-field" label="Description *"/>
                  </div>
                  <div class="mt-4">
                    <textarea
                        v-model="additionalInfo"
                        class="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                        placeholder="Additional Information"
                        rows="4"
                    ></textarea>
                  </div>
                  <div class="mt-4">
                    <FloatingLabelSelect v-model="transferNature" label="What is the nature of your fund transfer? *">
                      <option value="other-services-companies">Other services provided by companies</option>
                      <option value="non-resident-transfers">Non-resident transfers (any motive)</option>
                      <option value="personnel-services">Other services of personnel nature</option>
                      <option value="emigrant-account-transfer">Transfer between emigrant accounts (Portugal and exterior)</option>
                      <option value="education-services">Education services</option>
                      <option value="export-import-merchandise">Export/Import of merchandise</option>
                      <option value="private-current">Private current transfers from residents to non-residents</option>
                      <option value="resident-account-transfer">Transfers between resident accounts, in Portugal and exterior</option>
                      <option value="travel-tourism">Travel and tourism - travel expenses and tourism stays</option>
                    </FloatingLabelSelect>
                  </div>
                </div>

                <div>
                  <h3 class="text-sm font-medium text-gray-600 mb-2 tracking-wide">SUPPORTING TRANSFER DOCUMENTS (OPTIONAL)</h3>
                  <p class="text-sm text-gray-500 mb-4">You can upload multiple documents one by one</p>

                  <!-- List of attached documents -->
                  <div v-if="attachedDocuments.length > 0" class="mb-4 space-y-2">
                    <div v-for="(doc, index) in attachedDocuments" :key="index" class="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <div class="flex-1 mr-2">
                        <p class="text-gray-700 text-sm font-medium">{{ doc.file.name }}</p>
                        <input
                            v-model="doc.description"
                            type="text"
                            class="w-full mt-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                            placeholder="Document description"
                        />
                      </div>
                      <button type="button" class="text-red-500 px-2 py-1 rounded hover:bg-red-50" @click="removeDocument(index)">
                        <component :is="Trash" class="w-5 h-5"/>
                      </button>
                    </div>
                  </div>

                  <!-- File selection form -->
                  <Transition name="slide-down">
                    <div v-if="showDocumentForm" class="flex items-center gap-2 mb-4">
                      <div class="w-full space-y-3 transition duration-900">
                        <FloatingLabelInput v-model="documentDescription" input-id="doc-field" label="Document description"/>
                        <div class="relative">
                          <FloatingLabelFileInput
                              ref="fileInput"
                              @change="handleFileSelect"
                              accept=".pdf,.jpg,.jpeg,.png,.bmp"
                              class="mb-4"
                              label="Select File"
                          />
                        </div>
                      </div>
                      <button class="p-4 rounded-lg shadow text-red-400 hover:bg-red-50" @click="toggleDocumentForm(false)">
                        <component :is="Trash"/>
                      </button>
                    </div>
                  </Transition>

                  <!-- Add new file button -->
                  <button
                      class="w-full flex items-center justify-center px-4 py-2 border border-orange-500 text-primary rounded-md hover:bg-orange-50 transition-colors"
                      @click="toggleDocumentForm(true)"
                  >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"></path>
                    </svg>
                    Attach Documents
                  </button>
                  <p class="text-xs text-gray-400 mt-2">Allowed file formats: pdf, jpg, jpeg, png, bmp</p>
                </div>

              </div>
            </div>
          </Transition>
        </div>
      </div>

      <!-- Slide Panel -->
      <TransactionData v-show="isPanelOpen && !showTransactionConfirm && selected === 'european' && otpStep" v-model="isPanelOpen"   :transactionData="transactionData"  @otp-verified="onOtpVerified"/>
      <Transition name="slide-right">
        <TransactionConfirm v-show="showTransactionConfirm && selected === 'european' && isPanelOpen "
                            :loading="isLoading"
                            :transactionData="transactionData"
                            :transactionId="transactionId"
                            @update:modelValue="handleConfirmPanelClose"/>
      </Transition>
      <Transition name="slide-left">
        <TransferOther v-show="selected === 'international'"/>
      </Transition>

    </div>

    <!-- Transfer Buttons -->
    <div class="flex justify-center gap-x-3 mt-10" v-if="!isPanelOpen && selected === 'european'">
<!--      <button class="flex w-1/4 items-center justify-center gap-x-2 text-primary bg-white text-sm font-semibold py-2 px-6 rounded-md transition-colors text-lg tracking-wide shadow-md">-->
<!--        <component :is="Star" class="w-5 h-5"/>-->
<!--        Save Beneficiary-->
<!--      </button>-->

      <button class="w-1/4 bg-primary text-white font-semibold py-2 px-6 rounded-md transition-colors text-lg tracking-wide shadow-md"
              @click="submitTransfer();">
        TRANSFER
      </button>
    </div>
  </div>
</template>

<script setup>
import FloatingLabelInput from '@/components/ui/FloatingLabelInput.vue';
import FloatingLabelSelect from '@/components/ui/FloatingLabelSelect.vue';
import FloatingLabelFileInput from '@/components/ui/FloatingLabelFileInput.vue';
import BeneficiarySelector from '@/components/transfer/BeneficiarySelector.vue';
import {ref, watch, onMounted} from 'vue';
import {ArrowLeft, Euro, Globe, Trash} from 'lucide-vue-next';
import NewBeneficiaryModal from "@/components/transfer/NewBeneficiaryModal.vue";
import TransactionData from "@/components/transfer/TransactionData.vue";
import TransactionConfirm from "@/components/transfer/TransactionConfirm";
import TransferOther from "@/components/transfer/TransferOther";
import api from '@/services/api'; // axios instance
import debounce from 'lodash/debounce';


// Form data
const searchBeneficiary = ref('')
const transferValue = ref('')
const description = ref('')
const additionalInfo = ref('')
const transferNature = ref('')
const iban = ref('')
const swiftCode = ref('')
const beneficiaryName = ref('')
const showDocumentForm = ref(false)
const documentDescription = ref('')
const fileInput = ref(null)

const isPanelOpen = ref(false)
const showTransactionConfirm = ref(false)
const isLoading = ref(false)
const activeStep = ref(1)
const items = [
  {id:'european', label:'European Countries in Euros', icon:Euro},
  {id:'international', label:'Other Transfers', icon:Globe},
]
const selected = ref('european')

const selectedBeneficiary = ref({})
const beneficiaryList = ref([])
const isModalOpen = ref(false)
const transactionId = ref(null); // این ID از سرور بعد از تایید OTP میاد
const toggleDocumentForm = (value) => showDocumentForm.value = value
// const handleFileSelect = (event) => { const file = event.target.files[0]; if(file) console.log('Selected file:', file.name) }

const transactionData = ref({});
const otpStep = ref(false); // وقتی true شود، فرم OTP نمایش داده می‌شود

const submitTransfer = async () => {
  let missingFields = [];

  if (!iban.value) missingFields.push('IBAN');
  if (!swiftCode.value) missingFields.push('SWIFT Code');
  if (!beneficiaryName.value) missingFields.push('Beneficiary Name');
  if (!transferValue.value) missingFields.push('Value');
  if (!description.value) missingFields.push('Description');
  if (!transferNature.value) missingFields.push('Nature of Fund Transfer');

  if (missingFields.length > 0) {
    alert(`Please fill in the following required fields:\n- ${missingFields.join('\n- ')}`);
    return;
  }


  // آماده‌سازی داده‌ها
  transactionData.value = {
    account: selectedAccount.value,
    iban: iban.value,
    swift: swiftCode.value,
    beneficiaryName: beneficiaryName.value,
    value: transferValue.value,
    description: description.value,
    additionalInfo: additionalInfo.value,
    transferNature: transferNature.value,
    attachments: [...attachedDocuments.value] // اضافه کردن فایل‌ها
  }

  try {
    // ارسال درخواست OTP به سرور
    const res = await api.post('/transfer/send-otp', {
      account_id: selectedAccount.value.id,
      iban: iban.value,
      swift: swiftCode.value,
      beneficiary_name: beneficiaryName.value,
      value: transferValue.value,
      description: description.value,
      additional_info: additionalInfo.value,
      transfer_nature: transferNature.value,
    }, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    if (res.data.success) {
      isPanelOpen.value = true;
      otpStep.value = true; // فرم OTP باز شود
      showTransactionConfirm.value = false; // تا تایید OTP انجام شود
      transactionId.value = res.data.transaction_id; // ID تراکنش واقعی
      activeStep.value=2;
    }
  } catch (err) {
    console.error('Error sending OTP:', err);
    alert('Failed to send OTP. Please try again.');
  }
}



function openNewBeneficiaryForm() { isModalOpen.value = true }
function onBeneficiarySelected(value) {
  selectedBeneficiary.value = value;

  // پر کردن فرم با اطلاعات Beneficiary انتخاب شده
  iban.value = value.iban || '';
  swiftCode.value = value.swift || '';
  beneficiaryName.value = value.name || '';
  description.value = value.description || '';
  additionalInfo.value = value.additional_info || '';
}



// -----------------
// Accounts logic
// -----------------
const accounts = ref([])
const selectedAccountId = ref(null)
const selectedAccount = ref(null)
// const searchQuery = ref('');
// const beneficiaryResults = ref([]);


onMounted(async () => {
  try {
    const res = await api.get('/accounts', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    accounts.value = res.data
    if(accounts.value.length>0){
      selectedAccountId.value = accounts.value[0].id
      selectedAccount.value = accounts.value[0]
    }
  } catch(err){
    console.error('Error fetching accounts or beneficiaries:', err);
  }
})

watch(selectedAccountId, (newId) => {
  selectedAccount.value = accounts.value.find(acc => acc.id === newId) || null
})

watch(isPanelOpen, (newVal) => {
  if (!newVal) {
    showTransactionConfirm.value = false;
    otpStep.value = false; // OTP Step ریست شود
  }
})

// Debounced search
const searchBeneficiaries = async () => {
  try {
    const query = searchBeneficiary.value;
    const res = await api.get('/beneficiaries/search', {
      params: {
        q: query.length >= 2 ? query : '', // اگر کمتر از 2 کاراکتر، جستجو خالی میشه
        account_type: selected.value
      },
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    console.log("search",res.data);
    beneficiaryList.value = res.data;
  } catch (err) {
    console.error('Beneficiary search error:', err);
  }
};

const debouncedSearch = debounce(searchBeneficiaries, 300); // 300ms delay


onMounted(() => {
  searchBeneficiaries();
});



const onOtpVerified = () => {
  otpStep.value = false;            // فرم OTP بسته شود
  showTransactionConfirm.value = true; // صفحه تایید تراکنش نمایش داده شود
  activeStep.value = 3;             // مرحله سوم فعال شود
};


const handleConfirmPanelClose = () => {
  isPanelOpen.value = false;
  showTransactionConfirm.value = false;
  otpStep.value = true;

  // ریست فرم انتقال
  transferValue.value = '';
  description.value = '';
  additionalInfo.value = '';
  transferNature.value = '';
  iban.value = '';
  swiftCode.value = '';
  beneficiaryName.value = '';
  selectedBeneficiary.value = {};
  transactionData.value = {};
  transactionId.value = null;
  activeStep.value=1;
}


// File handling
const attachedDocuments = ref([]); // [{ file: File, description: String }]

// Sync with transactionData
watch(attachedDocuments, (newVal) => {
  transactionData.value.attachments = [...newVal]; // copy to ensure reactivity
}, { deep: true });



// const handleFileSelect = (event) => {
//   const file = event.target.files[0];
//   if (!file) return;
//
//   attachedDocuments.value.push({
//     file,
//     description: documentDescription.value || ''
//   });
//
//   // Reset input
//   documentDescription.value = '';
//   fileInput.value.value = '';
//   showDocumentForm.value = false;
// };


const handleFileSelect = (file) => {
  if (!file) return;

  attachedDocuments.value.push({
    file,
    description: documentDescription.value || ''
  });

  documentDescription.value = '';
  showDocumentForm.value = false;
};



const removeDocument = (index) => {
  attachedDocuments.value.splice(index, 1);
};


</script>

